import React, { useCallback } from 'react'
import styled from '@emotion/styled'

const C = {
  bg: '#0d1117', surface: '#161b22', border: '#21262d',
  name: '#e6edf3', contact: '#7d8590', sep: '#30363d',
  sectionKw: '#58a6ff', title: '#e6edf3', company: '#3fb950',
  date: '#7d8590', prompt: '#f78166', bullet: '#c9d1d9',
  summary: '#8b949e', skills: '#79c0ff',
  placeholder: '#3d444d', cursor: '#58a6ff',
}

const Shell = styled.div`flex:1;overflow-y:auto;background:${C.bg};display:flex;justify-content:center;align-items:flex-start;padding:2rem 1.5rem;min-width:0;@media(max-width:900px){flex:none;overflow-y:visible;min-height:60vh;}`
const Doc = styled.div`width:100%;max-width:700px;background:${C.bg};font-family:'SFMono-Regular','Menlo','Monaco','Cascadia Code',monospace;font-size:13px;line-height:1.7;color:${C.bullet};padding:2rem .5rem 4rem`
const NameField = styled.input`display:block;width:100%;font-family:inherit;font-size:1.4rem;font-weight:700;letter-spacing:.04em;text-transform:uppercase;color:${C.name};background:transparent;border:none;outline:none;padding:0;margin:0 0 .3rem;caret-color:${C.cursor};&::placeholder{color:${C.placeholder}}`
const ContactRow = styled.div`display:flex;flex-wrap:wrap;align-items:center;gap:0;color:${C.contact};margin-bottom:.1rem`
const ContactPiece = styled.input`font-family:inherit;font-size:.78rem;color:${C.contact};background:transparent;border:none;outline:none;padding:0;min-width:4ch;caret-color:${C.cursor};&::placeholder{color:${C.placeholder}}`
const Sep = styled.span`color:${C.sep};padding:0 .5em;user-select:none`
const Divider = styled.div`display:flex;align-items:center;gap:.6rem;margin:1.75rem 0 .85rem`
const DividerLabel = styled.span`font-size:.68rem;font-weight:700;letter-spacing:.2em;text-transform:uppercase;color:${C.sectionKw};white-space:nowrap;flex-shrink:0;&::before{content:'# ';opacity:.5}`
const DividerLine = styled.div`flex:1;height:1px;background:${C.border}`
const JobBlock = styled.div`margin-bottom:1.5rem`
const JobHeader = styled.div`display:flex;flex-wrap:wrap;align-items:baseline;gap:0;margin-bottom:.3rem`
const JobTitle = styled.input`font-family:inherit;font-size:.88rem;font-weight:700;color:${C.title};background:transparent;border:none;outline:none;padding:0;caret-color:${C.cursor};&::placeholder{color:${C.placeholder}}`
const JobDot = styled.span`color:${C.sep};padding:0 .4em`
const JobCompany = styled.input`font-family:inherit;font-size:.88rem;color:${C.company};background:transparent;border:none;outline:none;padding:0;caret-color:${C.cursor};&::placeholder{color:${C.placeholder}}`
const JobDates = styled.input`font-family:inherit;font-size:.78rem;color:${C.date};background:transparent;border:none;outline:none;padding:0 0 0 .75rem;caret-color:${C.cursor};&::placeholder{color:${C.placeholder}}`
const BulletArea = styled.textarea`display:block;width:100%;font-family:inherit;font-size:.83rem;color:${C.bullet};background:transparent;border:none;outline:none;resize:none;padding:0;line-height:1.75;overflow:hidden;caret-color:${C.cursor};&::placeholder{color:${C.placeholder}}`
const EduBlock = styled.div`margin-bottom:.8rem`
const EduLine = styled.div`display:flex;flex-wrap:wrap;align-items:baseline`
const SkillsArea = styled.textarea`display:block;width:100%;font-family:inherit;font-size:.83rem;color:${C.skills};background:transparent;border:none;outline:none;resize:none;padding:0;line-height:1.8;overflow:hidden;caret-color:${C.cursor};&::placeholder{color:${C.placeholder}}`
const SummaryArea = styled.textarea`display:block;width:100%;font-family:inherit;font-size:.83rem;color:${C.summary};background:transparent;border:none;outline:none;resize:none;padding:0;line-height:1.8;overflow:hidden;caret-color:${C.cursor};&::placeholder{color:${C.placeholder}}`
const EmptyState = styled.div`display:flex;flex-direction:column;align-items:center;justify-content:center;min-height:60vh;gap:.6rem;text-align:center`
const EmptyTitle = styled.div`font-size:.65rem;letter-spacing:.2em;text-transform:uppercase;color:${C.sep}`
const EmptyPrompt = styled.div`font-size:.8rem;color:${C.placeholder};max-width:260px;line-height:1.6`
const EmptyCursor = styled.span`display:inline-block;width:8px;height:1.1em;background:${C.cursor};opacity:.8;animation:blink 1.1s step-end infinite;vertical-align:text-bottom;margin-left:2px;@keyframes blink{0%,100%{opacity:.8}50%{opacity:0}}`

const autoResize = (el) => { if (!el) return; el.style.height = 'auto'; el.style.height = el.scrollHeight + 'px' }

export const ResumeDoc = ({ template, onChange }) => {
  const contact = template.sections.find(s => s.type === 'contact')?.sectionData
  const summarySection = template.sections.find(s => s.type === 'summary')
  const summaryText = summarySection?.content?.map(b => b.children?.[0]?.text || '').join('\n') || ''
  const experience = template.sections.find(s => s.type === 'experience')?.sectionData || []
  const education = template.sections.find(s => s.type === 'education')?.sectionData || []
  const skillsSection = template.sections.find(s => s.type === 'skills')
  const skillsText = skillsSection?.content?.map(b => b.children?.[0]?.text || '').join('\n') || ''
  const hasContent = !!(contact?.name || summaryText || experience.length || education.length || skillsText)

  const updateContact = useCallback((field, value) => {
    const updated = { ...template }
    updated.sections = updated.sections.map(s => s.type !== 'contact' ? s : { ...s, sectionData: { ...s.sectionData, [field]: value } })
    onChange(updated)
  }, [template, onChange])

  const updateSummary = useCallback((text) => {
    const updated = { ...template }
    updated.sections = updated.sections.map(s => s.type !== 'summary' ? s : { ...s, content: [{ type: 'paragraph', children: [{ text }] }] })
    onChange(updated)
  }, [template, onChange])

  const updateExpBullets = useCallback((idx, text) => {
    const updated = { ...template }
    updated.sections = updated.sections.map(s => {
      if (s.type !== 'experience') return s
      const exp = [...s.sectionData]
      exp[idx] = { ...exp[idx], highlights: text.split('\n').filter(Boolean).map(t => ({ type: 'paragraph', children: [{ text: t.replace(/^[>·•\-–]\s*/, '') }] })) }
      return { ...s, sectionData: exp }
    })
    onChange(updated)
  }, [template, onChange])

  const updateExpField = useCallback((idx, field, value) => {
    const updated = { ...template }
    updated.sections = updated.sections.map(s => {
      if (s.type !== 'experience') return s
      const exp = [...s.sectionData]; exp[idx] = { ...exp[idx], [field]: value }
      return { ...s, sectionData: exp }
    })
    onChange(updated)
  }, [template, onChange])

  const updateSkills = useCallback((text) => {
    const updated = { ...template }
    updated.sections = updated.sections.map(s => s.type !== 'skills' ? s : { ...s, content: [{ type: 'paragraph', children: [{ text }] }] })
    onChange(updated)
  }, [template, onChange])

  if (!hasContent) return (
    <Shell>
      <Doc>
        <EmptyState>
          <EmptyTitle>ark / resume</EmptyTitle>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '.55rem', marginBottom: '1.25rem' }}>
            {[
              { n: '1', color: '#58a6ff', text: 'fill your profile' },
              { n: '2', color: '#3fb950', text: 'paste a job url + fetch' },
              { n: '3', color: '#f78166', text: 'hit generate' },
            ].map(({ n, color, text }) => (
              <div key={n} style={{ display: 'flex', alignItems: 'center', gap: '.6rem', fontFamily: 'monospace', fontSize: '.78rem' }}>
                <span style={{ color, minWidth: '1.2rem' }}>{n}.</span>
                <span style={{ color: '#7d8590' }}>{text}</span>
              </div>
            ))}
          </div>
          <EmptyPrompt>your resume will render here<EmptyCursor /></EmptyPrompt>
        </EmptyState>
      </Doc>
    </Shell>
  )

  return (
    <Shell className="ark-resume-print">
      <Doc>
        <NameField value={contact?.name || ''} onChange={e => updateContact('name', e.target.value)} placeholder="Full Name" />
        <ContactRow>
          <ContactPiece value={contact?.email || ''} onChange={e => updateContact('email', e.target.value)} placeholder="email" style={{ width: Math.max((contact?.email?.length || 0) + 1, 8) + 'ch' }} />
          {contact?.phone && <><Sep>·</Sep><ContactPiece value={contact.phone} onChange={e => updateContact('phone', e.target.value)} style={{ width: Math.max(contact.phone.length + 1, 6) + 'ch' }} /></>}
          {contact?.location && <><Sep>·</Sep><ContactPiece value={contact.location} onChange={e => updateContact('location', e.target.value)} style={{ width: Math.max(contact.location.length + 1, 6) + 'ch' }} /></>}
          {contact?.links?.map((link, i) => <React.Fragment key={i}><Sep>·</Sep><ContactPiece value={link.url} onChange={() => {}} style={{ width: Math.max(link.url.length + 1, 6) + 'ch' }} /></React.Fragment>)}
        </ContactRow>

        {summaryText && <><Divider><DividerLine /></Divider><SummaryArea value={summaryText} onChange={e => { autoResize(e.target); updateSummary(e.target.value) }} ref={el => autoResize(el)} placeholder="Professional summary..." /></>}

        {experience.length > 0 && <>
          <Divider><DividerLabel>Experience</DividerLabel><DividerLine /></Divider>
          {experience.map((job, i) => {
            const bullets = (job.highlights || []).map(h => h.children?.[0]?.text || '').filter(Boolean).map(t => `> ${t}`).join('\n')
            return (
              <JobBlock key={i}>
                <JobHeader>
                  <JobTitle value={job.position || ''} onChange={e => updateExpField(i, 'position', e.target.value)} placeholder="Title" style={{ width: Math.max((job.position?.length || 0) + 1, 8) + 'ch' }} />
                  <JobDot>·</JobDot>
                  <JobCompany value={job.company || ''} onChange={e => updateExpField(i, 'company', e.target.value)} placeholder="Company" style={{ width: Math.max((job.company?.length || 0) + 1, 8) + 'ch' }} />
                  <JobDates value={`${job.startDate || ''} – ${job.endDate || 'Present'}`} onChange={e => { const [s, d] = e.target.value.split('–').map(x => x.trim()); updateExpField(i, 'startDate', s || ''); updateExpField(i, 'endDate', d || '') }} style={{ width: Math.max(((job.startDate?.length || 0) + (job.endDate?.length || 7) + 4), 14) + 'ch' }} />
                </JobHeader>
                <BulletArea value={bullets} onChange={e => { autoResize(e.target); updateExpBullets(i, e.target.value) }} ref={el => autoResize(el)} placeholder="· Key achievement or responsibility" />
              </JobBlock>
            )
          })}
        </>}

        {education.length > 0 && <>
          <Divider><DividerLabel>Education</DividerLabel><DividerLine /></Divider>
          {education.map((edu, i) => (
            <EduBlock key={i}>
              <EduLine>
                <JobTitle value={edu.degree && edu.field ? `${edu.degree} · ${edu.field}` : edu.degree || edu.field || ''} onChange={() => {}} placeholder="Degree · Field" style={{ width: Math.max(((edu.degree?.length || 0) + (edu.field?.length || 0) + 3), 16) + 'ch' }} />
                <JobDot>·</JobDot>
                <JobCompany value={edu.institution || ''} onChange={() => {}} placeholder="School" style={{ width: Math.max((edu.institution?.length || 0) + 1, 8) + 'ch' }} />
                <JobDates value={`${edu.startDate || ''} – ${edu.endDate || ''}`} onChange={() => {}} style={{ width: '14ch' }} />
              </EduLine>
              {edu.gpa && <ContactRow style={{ marginTop: '.1rem' }}><span style={{ color: '#888', fontSize: '.68rem', fontFamily: 'monospace' }}>GPA {edu.gpa}</span></ContactRow>}
            </EduBlock>
          ))}
        </>}

        {skillsText && <>
          <Divider><DividerLabel>Skills</DividerLabel><DividerLine /></Divider>
          <SkillsArea value={skillsText} onChange={e => { autoResize(e.target); updateSkills(e.target.value) }} ref={el => autoResize(el)} placeholder="Python · TypeScript · React · AWS" />
        </>}
      </Doc>
    </Shell>
  )
}
